import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Plus, Trash2, Loader2, UserPlus } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getAdminUsers, createAdminUser, deleteAdminUser } from '@/lib/api';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Manage participants, view settings' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access to dashboard and participants' },
];

export default function AdminTeam() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'admin', display_name: '' });

  const fetchAdmins = () => {
    getAdminUsers().then(setAdmins).catch(() => toast.error('Failed to load team')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async () => {
    if (!form.username || !form.password || !form.display_name) {
      toast.error('Please fill in all fields'); return;
    }
    setSaving(true);
    try {
      await createAdminUser(form);
      toast.success(`${form.display_name} added to team`);
      setForm({ username: '', password: '', role: 'admin', display_name: '' });
      setShowForm(false);
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await deleteAdminUser(id);
      toast.success(`${name} removed`);
      fetchAdmins();
    } catch { toast.error('Failed to remove user'); }
  };

  const inputClass = "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 focus:border-rtb-purple focus:ring-1 focus:ring-rtb-purple/20 h-11 rounded-lg";
  const selectClass = "w-full h-11 bg-white/[0.04] border border-white/[0.08] text-white px-4 rounded-lg focus:border-rtb-purple focus:outline-none text-sm";

  return (
    <AdminLayout title="Team Management">
      <div className="flex items-center justify-between mb-6">
        <p className="text-white/40 text-sm">Manage admin users and their roles.</p>
        <Button onClick={() => setShowForm(!showForm)} className="bg-rtb-purple text-white hover:bg-rtb-purple/90 rounded-lg text-sm px-4" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Team Member
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl mb-6">
          <h3 className="font-oswald text-base font-bold text-white mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-rtb-purple" /> New Team Member
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/50 mb-1.5 block text-sm">Display Name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className={inputClass} placeholder="John Doe" data-testid="new-admin-name" />
            </div>
            <div>
              <Label className="text-white/50 mb-1.5 block text-sm">Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                className={inputClass} placeholder="johndoe" data-testid="new-admin-username" />
            </div>
            <div>
              <Label className="text-white/50 mb-1.5 block text-sm">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={inputClass} placeholder="Min 6 characters" data-testid="new-admin-password" />
            </div>
            <div>
              <Label className="text-white/50 mb-1.5 block text-sm">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className={selectClass} data-testid="new-admin-role">
                {ROLES.map(r => (
                  <option key={r.value} value={r.value} className="bg-charcoal">{r.label} — {r.desc}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleCreate} disabled={saving} className="bg-rtb-purple text-white hover:bg-rtb-purple/90 rounded-lg text-sm" data-testid="save-admin-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
            </Button>
            <Button onClick={() => setShowForm(false)} variant="outline" className="border-white/10 text-white/50 hover:text-white rounded-lg text-sm">Cancel</Button>
          </div>
        </motion.div>
      )}

      {/* Team List */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-rtb-purple animate-spin" /></div>
      ) : admins.length === 0 ? (
        <div className="p-12 bg-white/[0.04] border border-white/[0.06] rounded-xl text-center">
          <ShieldCheck className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/30">No additional team members. You are the super admin.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {admins.map(a => (
            <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="p-4 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-between"
              data-testid={`admin-user-${a.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                  a.role === 'super_admin' ? 'bg-rtb-purple/10 text-rtb-purple' :
                  a.role === 'admin' ? 'bg-rtb-green/10 text-rtb-green' :
                  'bg-white/5 text-white/40'
                }`}>
                  {a.display_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-medium text-white/90">{a.display_name}</p>
                  <p className="text-white/30 text-sm">@{a.username} · <span className="capitalize">{a.role?.replace('_', ' ')}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {a.last_login && (
                  <span className="text-white/20 text-xs">Last login: {new Date(a.last_login).toLocaleDateString()}</span>
                )}
                {a.role !== 'super_admin' && (
                  <button onClick={() => handleDelete(a.id, a.display_name)}
                    className="text-red-400/50 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-500/5"
                    data-testid={`delete-admin-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
